"use client";

import { useMemo } from "react";
import { useTranslations } from "next-intl";

// Structural type — avoids a direct zod dep in this package (api-client
// re-exports the validated payload schema; ZodError shape is portable).
// `path` widened to `PropertyKey[]` to match zod's $ZodIssue exactly;
// we filter to string|number inside zodIssueToMessage.
interface ZodErrorShape {
  issues: ReadonlyArray<{ path?: ReadonlyArray<PropertyKey>; message?: string }>;
}

interface MutationErrorShape {
  name?: string;
  status?: number;
  issues?: Array<{ path?: Array<string | number>; message?: string }>;
  problem?: { type?: string; title?: string; detail?: string; user_action?: string };
}

/**
 * Translates a mutation error (or a live-validation ZodError) into the
 * user-facing string the design-panel's `errMessage` slot renders.
 *
 * - ZodError clientside (bad URL etc.) — render first issue with path
 * - HTTP 429 — surface backend's `user_action` or generic rate-limit
 * - HTTP 402 vanity-stack-limit — open the upgrade dialog AND show body
 * - HTTP 400 / fallback — surface backend's `user_action` or generic
 */
export function useErrMessage({
  mutationError,
  liveValidation,
  vanityUpgradeOpen,
  onOpenVanityUpgrade,
}: {
  mutationError: unknown;
  liveValidation: ZodErrorShape | null;
  vanityUpgradeOpen: boolean;
  onOpenVanityUpgrade: () => void;
}): string | null {
  const t = useTranslations("generator");
  return useMemo<string | null>(() => {
    const err = mutationError as MutationErrorShape | null;
    if (!err) {
      if (liveValidation && liveValidation.issues.length > 0) {
        return zodIssueToMessage(liveValidation.issues, t);
      }
      return null;
    }
    if (err.name === "ZodError" && Array.isArray(err.issues) && err.issues.length > 0) {
      return zodIssueToMessage(err.issues, t);
    }
    const specific = err.problem?.user_action ?? err.problem?.detail ?? err.problem?.title;
    if (err.status === 429) return specific ?? t("error.rateLimited");
    if (err.status === 402 && err.problem?.type?.includes("vanity-stack-limit")) {
      if (!vanityUpgradeOpen) onOpenVanityUpgrade();
      return specific ?? t("pro.upgrade.body");
    }
    if (err.status === 400) return specific ?? t("error.invalidPayload");
    return specific ?? t("error.unknown");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutationError, liveValidation, vanityUpgradeOpen, t]);
}

function zodIssueToMessage(
  issues: ReadonlyArray<{ path?: ReadonlyArray<PropertyKey>; message?: string }>,
  t: ReturnType<typeof useTranslations>,
): string {
  const first = issues[0]!;
  const pathSegs = (first.path ?? [])
    .filter((p): p is string | number => typeof p === "string" || typeof p === "number")
    .map(String);
  const path = pathSegs.filter((p) => p !== "").join(".");
  const detail = path ? `${path}: ${first.message ?? ""}`.trim() : (first.message ?? "");
  return t("error.validationFailed", { detail });
}
